import { AfterViewInit, Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core';
import {
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validators
} from '@angular/forms';
import { Dictionary } from '@ngrx/entity';
import { Store } from '@ngrx/store';
import { flatMap as _flatMap, get as _get } from 'lodash';
import { combineLatest, Observable, of, Subject } from 'rxjs';
import { filter, map, mergeMap, take, takeUntil, withLatestFrom } from 'rxjs/operators';

import {
  addVehicleInfoQuery,
  AddVehicleInitiateVehicleModel,
  addVehicleQuoteQuery
} from '@amfam/policy/add-vehicle/data-access';
import { Vehicle } from '@amfam/policy/models';
import { BrandSelectors } from '@amfam/shared/utility/brand';
import { fromRouterActions } from '@amfam/shared/utility/navigation';
import { CopyService } from '@amfam/shared/utility/shared-services';
import { DsModalService, LoadingSpinnerService } from '@amfam/ui-kit';

import * as VehicleInfoActions from '../+state/add-vehicle-feature.actions';
import * as addVehicleFeatureSelector from '../+state/add-vehicle-feature.selectors';
import { DriverInfoModel } from '../models';
import { AddVehicleFeatureService } from '../services/add-vehicle-feature.service';

@Component({
  selector: 'ds-vehicle-secondary-driver-selection',
  templateUrl: './vehicle-secondary-driver-selection.component.html',
  styleUrls: ['./vehicle-secondary-driver-selection.component.scss'],
  standalone: false
})
export class VehicleSecondaryDriverSelectionComponent implements OnInit, AfterViewInit, OnDestroy {
  @Output() cancelEvent = new EventEmitter<boolean>();

  public switchDriverModalId = this.modalService.createModalId();
  public driversList$ = this.store.select(addVehicleInfoQuery.selectNewVehicleDrivers);
  public assignedPrimaryDriverList$ = this.store.select(
    addVehicleFeatureSelector.selectCurrentVehiclePrimaryDrivers
  );
  public vehiclesList$ = this.store.select(addVehicleInfoQuery.selectNewVehicleList);
  public remainingdriverNamesList$ = this.driversList$.pipe(
    withLatestFrom(this.assignedPrimaryDriverList$),
    map(([drivers, primaryDriverNamesList]) =>
      drivers.filter(
        driver =>
          !primaryDriverNamesList.find(primaryDriverName => primaryDriverName === driver.name)
      )
    ),
    map(driverList => _flatMap(driverList, driver => driver.name))
  );
  public alldriverNamesList$ = this.driversList$.pipe(
    map(driverList => _flatMap(driverList, driver => driver.name))
  );
  isPartner$ = this.store.select(BrandSelectors.selectIsPartner);
  public driversSelectionForm: UntypedFormGroup;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public assignedVehicleWithDrivers: Dictionary<any> = {};
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public assignedDriversWithVehicles: Dictionary<any> = {};
  public driverForAssignment: string;
  public vehicleForAssignment: Vehicle;
  private stop$: Subject<void> = new Subject<void>();

  constructor(
    private copyService: CopyService,
    private store: Store,
    private formBuilder: UntypedFormBuilder,
    private spinnerService: LoadingSpinnerService,
    private addVehicleFeatureService: AddVehicleFeatureService,
    private modalService: DsModalService
  ) {}

  ngOnInit(): void {
    this.spinnerService.stop();
    this.buildControls(this.formBuilder.group({}), this.vehiclesList$, this.alldriverNamesList$);
    this.handleError();
  }

  ngAfterViewInit() {
    this.driversList$
      .pipe(withLatestFrom(this.vehiclesList$), take(1))
      .subscribe(([driversList, vehicleList]) => {
        this.store.dispatch(
          VehicleInfoActions.addVehicleSendAnalytics({
            payload: {
              pageAnalyticName:
                _get(driversList, 'length', 0) === 2 * _get(vehicleList, 'length', 0)
                  ? 'SecondaryDriverSelection'
                  : 'RemainingDriverSelection'
            }
          })
        );
      });
  }

  vehicleAssignment({ driverName, vehicle }) {
    if (
      (!!this.assignedVehicleWithDrivers[driverName] && this.isVehicleMatch(vehicle.vin)) ||
      this.isVehicleMatch(vehicle.vin)
    ) {
      // Driver already assigned show the modal
      this.vehicleForAssignment = vehicle;
      this.driverForAssignment = driverName;
      this.modalService.open(this.switchDriverModalId);
    } else {
      this.assignedVehicleWithDrivers[driverName] = { ...vehicle, driverName };
    }
  }

  driverAssignment({ driverName, vehicle }) {
    if (
      (!!this.assignedDriversWithVehicles[vehicle.vin] && this.isDriverMatch(driverName)) ||
      this.isDriverMatch(driverName)
    ) {
      // Driver already assigned show the modal
      this.vehicleForAssignment = vehicle;
      this.driverForAssignment = driverName;
      this.modalService.open(this.switchDriverModalId);
    } else {
      this.assignedDriversWithVehicles[vehicle.vin] = { ...vehicle, driverName };
    }
  }

  isDriverMatch(driverName) {
    return !!_flatMap(
      Object.values(this.assignedDriversWithVehicles),
      vehicle => vehicle.driverName
    ).find(driver => driver === driverName);
  }

  isVehicleMatch(vehicleVin) {
    return !!_flatMap(Object.values(this.assignedVehicleWithDrivers), vehicle => vehicle.vin).find(
      vehicle => vehicle === vehicleVin
    );
  }

  getMatchedVehicleVin(
    assignedDriversWithVehicles: Dictionary<Vehicle & { driverName: string }>,
    driverName: string
  ) {
    return _flatMap(Object.values(assignedDriversWithVehicles), vehicle => ({
      vin: vehicle.vin,
      driverName: vehicle.driverName
    })).find(vehicle => vehicle.driverName === driverName);
  }

  getMatchedDriver(
    assignedVehicleWithDrivers: Dictionary<Vehicle & { driverName: string }>,
    vin: string
  ) {
    return _flatMap(Object.values(assignedVehicleWithDrivers), vehicle => ({
      vin: vehicle.vin,
      driverName: vehicle.driverName
    })).find(vehicle => vehicle.vin === vin);
  }

  switchDrivers(currentDriverName: string, currentVehicleInSelection: Vehicle) {
    const currentDriverNotAssigned = !!this.assignedVehicleWithDrivers[currentDriverName];
    const currentVehicleNotAssigned =
      !!this.assignedDriversWithVehicles[currentVehicleInSelection.vin];

    if (
      (!!this.assignedVehicleWithDrivers[currentDriverName] &&
        this.isVehicleMatch(currentVehicleInSelection.vin)) ||
      this.isVehicleMatch(currentVehicleInSelection.vin)
    ) {
      const previousAssignedVehicle =
        this.assignedVehicleWithDrivers[
          this.getMatchedDriver(
            <Dictionary<Vehicle & { driverName: string }>>this.assignedVehicleWithDrivers,
            currentVehicleInSelection.vin
          ).driverName
        ];

      if (currentDriverNotAssigned) {
        this.driversSelectionForm.controls[previousAssignedVehicle.driverName].setValue(
          this.assignedVehicleWithDrivers[currentDriverName].vin
        );
        this.assignedVehicleWithDrivers[previousAssignedVehicle.driverName] =
          this.assignedVehicleWithDrivers[currentDriverName];
      } else {
        delete this.assignedVehicleWithDrivers[previousAssignedVehicle.driverName];
        this.driversSelectionForm.controls[previousAssignedVehicle.driverName].setValue('');
      }

      this.assignedVehicleWithDrivers[currentDriverName] = {
        ...currentVehicleInSelection,
        driverName: currentDriverName
      };
    } else if (
      (currentVehicleNotAssigned && this.isDriverMatch(currentDriverName)) ||
      this.isDriverMatch(currentDriverName)
    ) {
      const previousAssignedVehicle =
        this.assignedDriversWithVehicles[
          this.getMatchedVehicleVin(
            <Dictionary<Vehicle & { driverName: string }>>this.assignedDriversWithVehicles,
            currentDriverName
          ).vin
        ];
      if (currentVehicleNotAssigned) {
        this.driversSelectionForm.controls[previousAssignedVehicle.vin].setValue(
          this.assignedDriversWithVehicles[currentVehicleInSelection.vin].driverName
        );
        this.assignedDriversWithVehicles[previousAssignedVehicle.vin] =
          this.assignedDriversWithVehicles[currentVehicleInSelection.vin];
      } else {
        delete this.assignedDriversWithVehicles[previousAssignedVehicle.vin];
        this.driversSelectionForm.controls[previousAssignedVehicle.vin].setValue('');
      }

      this.assignedDriversWithVehicles[currentVehicleInSelection.vin] = {
        ...currentVehicleInSelection,
        driverName: currentDriverName
      };
    }
  }

  cancelDriverAssignment(currentDriverName: string, currentVehicleInSelection: Vehicle) {
    const currentDriverNotAssigned = !!this.assignedVehicleWithDrivers[currentDriverName];
    const currentVehicleNotAssigned =
      !!this.assignedDriversWithVehicles[currentVehicleInSelection.vin];

    if (
      (currentDriverNotAssigned && this.isVehicleMatch(currentVehicleInSelection.vin)) ||
      this.isVehicleMatch(currentVehicleInSelection.vin)
    ) {
      this.driversSelectionForm.controls[currentDriverName].setValue('');
      delete this.assignedVehicleWithDrivers[currentDriverName];
    } else if (
      (currentVehicleNotAssigned && this.isDriverMatch(currentDriverName)) ||
      this.isDriverMatch(currentDriverName)
    ) {
      this.driversSelectionForm.controls[currentVehicleInSelection.vin].setValue('');
      delete this.assignedDriversWithVehicles[currentVehicleInSelection.vin];
    }
  }

  submitDriverSelectionForm() {
    const driverAssigmentArr: DriverInfoModel[] = [];
    this.driversList$
      .pipe(
        take(1),
        withLatestFrom(
          this.addVehicleFeatureService.hasDisqualifyingAnswers(),
          this.store.select(BrandSelectors.selectIsPartner)
        )
      )
      .subscribe(([driversList, hasHardStop, isPartner]) => {
        for (const key of Object.keys(this.assignedVehicleWithDrivers)) {
          driverAssigmentArr.push({
            name: key,
            vin: this.assignedVehicleWithDrivers[key].vin,
            primary: false,
            publicId: String(driversList.find(driver => driver?.name === key)?.publicId),
            driverId: String(driversList.find(driver => driver?.name === key)?.publicId),
            vehicleId: this.assignedVehicleWithDrivers[key].fixedId
          });
        }
        this.store.dispatch(
          VehicleInfoActions.setVehicleSecondaryDriversInfo({
            driversInfo: driverAssigmentArr
          })
        );
        if (!isPartner && hasHardStop) {
          this.spinnerService.start({
            blockActions: true
          });
          this.store.dispatch(
            fromRouterActions.Go({
              path: ['policies/add-vehicle/agent-help']
            })
          );
        } else if (isPartner) {
          this.spinnerService.start({
            blockActions: true
          });
          this.store.dispatch(
            fromRouterActions.Go({
              path: ['policies/add-vehicle/uw-questions']
            })
          );
        } else {
          this.spinnerService.start({
            blockActions: true,
            secondaryMessage: this.copyService.getCopy('policy.quoteCalculatingLoadingSpinnerText')
          });
          this.store.dispatch(
            VehicleInfoActions.addVehicleSendAnalytics({
              payload: { pageAnalyticName: 'QuoteCalculating' }
            })
          );
          this.store.dispatch(VehicleInfoActions.addVehicleRate());
        }
      });
  }

  goBackOneStep() {
    this.store.dispatch(fromRouterActions.Back());
  }

  ngOnDestroy() {
    this.stop$.next();
    this.stop$.complete();
  }

  /**
   * @author: Abhishek Singh
   * @param form : Takes a form group and adds the new controls to the form
   * @returns FormGroup : Return the form which has the new controls and the initialized values.
   */
  private buildControls(
    form: UntypedFormGroup,
    vehiclesList$: Observable<AddVehicleInitiateVehicleModel[]>,
    driverNameList$: Observable<string[]>
  ) {
    combineLatest(vehiclesList$, driverNameList$)
      .pipe(
        mergeMap(([vehicles, driverNames]) => {
          if (driverNames.length < 2 * vehicles.length) {
            return of(driverNames).pipe(
              map(driverNameList => {
                for (const driverName of driverNameList) {
                  form.addControl(driverName, new UntypedFormControl('', Validators.required));
                }
                return form;
              })
            );
          } else if (driverNames.length === 2 * vehicles.length) {
            return of(vehicles).pipe(
              map(vehicleList => {
                for (const vehicle of vehicleList) {
                  form.addControl(vehicle.vin, new UntypedFormControl('', Validators.required));
                }
                return form;
              })
            );
          }
        }),
        filter(formGroup => !!formGroup),
        take(1)
      )
      .subscribe(formGroup => {
        this.driversSelectionForm = formGroup;
      });
  }

  private handleError() {
    this.store
      .select(addVehicleQuoteQuery.selectNewVehicleRateError)
      .pipe(takeUntil(this.stop$))
      .subscribe(vehicleFeaturesError => {
        if (vehicleFeaturesError) {
          this.spinnerService.stop();
          this.store.dispatch(
            fromRouterActions.Go({
              path: ['policies/add-vehicle/agent-help']
            })
          );
        }
      });
  }
}
